import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Form, Formik, Field } from "formik";
import NipInput from "../NipInput/NipInput";
import NipInputSession from "../NipInput/NipInputSession";
import NipSelect from "../NipInput/NipSelect";
import Button from "../Button/Button";
import Toggle from "../Toggle/Toggle";
import { NameEnquirySchema } from "../../utils/validation";
import XmlSection from "../XmlViewer/XmlViewer";
import generateNumber from "../../utils/Functions/GenerateRandomNo";
import formatDate from "../../utils/Functions/FormatDate";
import { opt } from "../../utils/Functions/Opt";

const NameEnquiry = (props) => {
  const {
    goToFundTransfer,
    clearSuccessState,
    isSending,
    sendSuccess,
    nameEnquiryData,
    sendNameEnquiryValues,
  } = props;

  const [state, setState] = useState({
    nameEnquiry: {},
    retryCheck: false,
  });

  let newTimeStamp = formatDate();

  useEffect(() => {
    generateNumber(setState, state);
    // eslint-disable-next-line
  }, []);

  const defaultValues = {
    session_id: process.env.REACT_APP_API_SESSION_ID,
    destination_code: process.env.REACT_APP_API_DESTINATION_CODE,
    channel_code: process.env.REACT_APP_API_CHANNEL_CODE,
    account_number: process.env.REACT_APP_API_ACCOUNT_NUMBER,
  };

  const fixedValues = {
    time_stamp: newTimeStamp,
    random_number: state.random_number,
  };

  const setDefaultValues = () => {
    setState(JSON.parse(JSON.stringify(state)));
    setState({
      ...state,
      nameEnquiry: defaultValues,
      random_number: state.random_number,
    });
  };

  const removeDefaultValues = () => {
    setState({
      nameEnquiry: {},
      random_number: state.random_number,
    });
  };

  const resetForm = () => {
    clearSuccessState();
    setState({
      nameEnquiry: {},
      retryCheck: true,
      random_number: state.random_number,
    });
  };

  const initialValues = {
    sendersFinancialInstitutionCode: state.nameEnquiry.session_id || "",
    destinationInstitutionCode: state.nameEnquiry.destination_code || "",
    channelCode: state.nameEnquiry.channel_code || "",
    accountNumber: state.nameEnquiry.account_number || "",
    randomNumber: state.random_number || "",
    timeStamp: fixedValues.time_stamp || "",
  };

  const handleSubmit = (values) => {
    const name = "name-enquiry";
    const payload = {
      sendersFinancialInstitutionCode: values.sendersFinancialInstitutionCode,
      destinationInstitutionCode: values.destinationInstitutionCode,
      channelCode: values.channelCode,
      accountNumber: values.accountNumber,
      randomNumber: state.random_number,
      timestamp: fixedValues.time_stamp,
    };
    sendNameEnquiryValues(name, payload);
  };

  const xml = (values) => {
    const updateXml = `<?xml version="1.0" encoding='UTF-8'?><NESingleRequest><SessionID>${
      initialValues.sendersFinancialInstitutionCode ||
      values.sendersFinancialInstitutionCode
    }${fixedValues.time_stamp}${state.random_number}
    </SessionID><DestinationInstitutionCode>${
      initialValues.destinationInstitutionCode ||
      values.destinationInstitutionCode
    }</DestinationInstitutionCode><ChannelCode>${
      initialValues.channelCode || values.channelCode
    }</ChannelCode><AccountNumber>${
      initialValues.accountNumber || values.accountNumber
    }</AccountNumber></NESingleRequest>`;
    return updateXml;
  };

  const xmlSuccess = nameEnquiryData && nameEnquiryData;

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={NameEnquirySchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => {
          const disable =
            values.sendersFinancialInstitutionCode === "" ||
            values.destinationInstitutionCode === "" ||
            values.channelCode === "" ||
            values.accountNumber === "";

          return (
            <Form id="form">
              <div className="nip-home-interaction-env">
                <div className="nip-home-interaction-env-left">
                  <div className="nip-home-interaction-env-left-circle">i</div>
                  <div className="title">Name Enquiry</div>
                  <div className="details">
                    Run tests, requests and responses, make API Calls and
                    perform other related activities.
                  </div>
                  <div className="set-default-box">
                    <span className="set-default-box-word">
                      Use default figure for all entries
                    </span>
                    <div className="set-default-box-icon">
                      <Toggle
                        setDefaultValues={() => setDefaultValues()}
                        removeDefaultValues={() => removeDefaultValues()}
                        retryCheck={state.retryCheck}
                      />
                    </div>
                  </div>
                  <div className="nip-form">
                    <Field
                      name="sendersFinancialInstitutionCode"
                      placeholder="Enter Institution Code"
                      label="Session ID"
                      detail="This is made up of sender's institution code, date and time, and character unique number."
                      title2="Date and time"
                      value2={fixedValues.time_stamp}
                      title3="Unique number"
                      value3={fixedValues.random_number}
                      component={NipInputSession}
                    />
                    <Field
                      name="destinationInstitutionCode"
                      placeholder="Enter Destination Code"
                      label="Destination Code"
                      component={NipInput}
                    />
                    <Field name="channelCode">
                      {(val) => (
                        <NipSelect
                          label="Channel Code"
                          placeholder="Select Channel Code"
                          options={opt}
                          {...val}
                        />
                      )}
                    </Field>
                    <Field
                      name="accountNumber"
                      placeholder="Enter Account Number"
                      label="Account Number"
                      component={NipInput}
                    />
                    {!sendSuccess ? (
                      <Button
                        testId="submitNE"
                        type="submit"
                        width="100%"
                        height="45px"
                        disabled={disable || isSending}
                        word={isSending ? "SENDING..." : "SEND REQUEST"}
                      />
                    ) : (
                      <Button
                        testId="proceedToFT"
                        type="button"
                        width="100%"
                        height="45px"
                        onClick={() => goToFundTransfer()}
                        word={"PROCEED"}
                      />
                    )}
                  </div>
                </div>

                <XmlSection
                  xml={sendSuccess ? xmlSuccess : xml(values)}
                  url={"api.nibss.com/transaction/initialize"}
                  onClick={() => resetForm()}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

NameEnquiry.propTypes = {
  getSuccess: PropTypes.bool,
  goToFundTransfer: PropTypes.func,
  isGetting: PropTypes.bool,
  isSending: PropTypes.bool,
  nameEnquiryData: PropTypes.any,
  nameEnquiryValues: PropTypes.any,
  sendNameEnquiryValues: PropTypes.func,
  sendSuccess: PropTypes.bool,
  clearSuccessState: PropTypes.func,
};

export default NameEnquiry;
